import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Search, Eraser, ArrowLeft, FileText, Zap, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const FindAndReplacePage = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('find-replace-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const handleReplace = () => {
    if (!inputText || !findText) {
      setOutputText(inputText);
      return;
    }

    let result;
    if (caseSensitive) {
      // Case sensitive replace - replace all occurrences
      result = inputText.split(findText).join(replaceText);
    } else {
      // Case insensitive replace - replace all occurrences
      const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      result = inputText.replace(regex, replaceText);
    }
    
    setOutputText(result);
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setFindText('');
    setReplaceText('');
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('find-replace-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Find and Replace Text Online — Quick Text Editor Tool | AllSmallTooLS</title>
        <meta name="description" content="Find and replace words or phrases in your text instantly. Simple online tool for quick text editing." />
        <meta name="keywords" content="find and replace, text editor online, replace words in text, text replace tool" />
        <link rel="canonical" href="https://allsmalltools.com/text-find-replace" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to find and replace tool anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your find and replace tool for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <Search className="h-8 w-8 text-green-500" />
            <span>Find and Replace</span>
          </h1>
          <p className="text-gray-600">Find and replace text in your content instantly</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Input Box */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Input Text</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter your text here..."
              className="w-full flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-gray-900"
              style={{ minHeight: '400px' }}
            />
          </CardContent>
        </Card>

        {/* Find and Replace Controls */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Find & Replace</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-center">
            <div className="flex flex-col space-y-4">
              {/* Find Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Find
                </label>
                <input
                  type="text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  placeholder="Text to find"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                />
              </div>

              {/* Replace Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Replace with
                </label>
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replacement text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                />
              </div>

              {/* Case Sensitive Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="caseSensitive"
                  checked={caseSensitive}
                  onChange={(e) => setCaseSensitive(e.target.checked)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <label htmlFor="caseSensitive" className="text-sm text-gray-700">
                  Case sensitive
                </label>
              </div>

              {/* Replace Button */}
              <Button
                onClick={handleReplace}
                className="w-full justify-center bg-green-600 hover:bg-green-700 text-white"
              >
                <Search className="h-4 w-4 mr-2" />
                Replace All
              </Button>
              
              {/* Clear Button */}
              <Button
                onClick={handleClear}
                variant="outline"
                className="w-full justify-center border-2 border-gray-400 text-gray-700 hover:bg-gray-100"
              >
                <Eraser className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output Box */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Output Text</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <textarea
              value={outputText}
              readOnly
              placeholder="Result will appear here..."
              className="w-full flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 resize-none"
              style={{ minHeight: '400px' }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <Search className="h-6 w-6 text-green-600" />
            <div className="text-sm text-green-700">
              <strong>How to use:</strong> Enter your text in the left box, specify what to find and what to replace it with in the middle section, then click "Replace All" to see the result in the right box.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Why Use Our Find and Replace Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Find and Replace Tool?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Search className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Bulk Text Replacement</h3>
              <p className="text-gray-600">
                Find and replace text across entire documents instantly. Our tool replaces all occurrences at once, saving time on repetitive text editing tasks.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Case-Sensitive Options</h3>
              <p className="text-gray-600">
                Toggle case-sensitive search to find exact matches or broad replacements. Our find and replace tool adapts to your text editing needs.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Instant Processing</h3>
              <p className="text-gray-600">
                Replace text instantly with one click. No manual searching through documents. Our tool processes find and replace operations immediately.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Need to find and replace text in documents, code, or content? Our free text replacement tool makes bulk editing simple. Perfect for correcting typos, updating terminology, or reformatting content. Search and replace text with precision and speed. Try our find and replace tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/online-notepad" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Online Notepad
              </Link>
              <Link to="/text-case-converter" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Case Converter
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Find and Replace Text Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Find and Replace Text Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Paste Your Text:</strong> Enter or paste your text in the input box on the left. The find and replace tool works with text of any length.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Enter Search Terms:</strong> Type the text you want to find in the \"Find\" field. Enter replacement text in the \"Replace with\" field.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Set Options:</strong> Check \"Case sensitive\" if needed for exact matching. Leave unchecked for broader text replacement across different cases.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Replace All Text:</strong> Click \"Replace All\" to process. View the result in the output box with all occurrences replaced instantly.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is find and replace free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our find and replace tool is completely free. Search and replace text unlimited times without registration or fees.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What is case-sensitive search?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Case-sensitive search only finds exact matches including uppercase and lowercase. Unchecked, the tool finds \"Hello\", \"hello\", and \"HELLO\" when searching for \"hello\".</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I replace multiple words at once?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">The tool replaces one search term at a time. For multiple replacements, process each term separately. All occurrences of the search term are replaced simultaneously.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is my text data private?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! All text processing happens in your browser. Your text is never uploaded to any server. Complete privacy for all find and replace operations.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default FindAndReplacePage;
